import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "../components/tailwind/modal/Modal";
import { Button, ToggleCheckbox, ToggleLabel } from "../components/tailwind";
import { useGetCustomerQuery } from "../api/customerApi";
import { renderLanguageFromCode } from "../customers/customerlanguages";
import { useGetAuthQuery } from "../api/authApi";
import { MachineTranslateTaskQueue } from "../api/machineTranslationApi";
import { useSelector } from "react-redux";
import { RootState } from "../utils/store";
import { NoTranslateCell } from "../file-viewer/excel/types";

type Props = Parameters<typeof Modal>[0] & {
  sourceFile: File;
  sourceLanguage: string;
  targetLanguages: string[];
  sourceContext: string;
  noTranslateCells?: Omit<NoTranslateCell, "locked">[];
  languageCells?: { [language: string]: number };
  disclaimer?: React.ReactNode;
  allowSendAllLanguagesInOneRequest?: boolean;
  configGroupId?: number;
  onSuccess?: () => void;
};

export const MachineTranslateAndProofreadModal: React.FC<Props> = ({
  dialogRef,
  sourceFile,
  sourceLanguage,
  targetLanguages,
  sourceContext,
  noTranslateCells,
  languageCells,
  disclaimer,
  allowSendAllLanguagesInOneRequest,
  configGroupId,
  onSuccess,
  ...modalProps
}) => {
  const [disableModal, setDisableModal] = useState(false);
  const { data: auth, isLoading: isAuthLoading } = useGetAuthQuery();
  const { data: customer } = useGetCustomerQuery();
  const token = useSelector((state: RootState) => state.auth.token);
  const [urgent, setUrgent] = useState(false);
  const [deliverInOneFile, setDeliverInOneFile] = useState(false);
  const [additionalInstructions, setAdditionalInstructions] = useState("");
  const [email, setEmail] = useState("");

  const renderLanguage = (languageCode: string): string => {
    return (
      renderLanguageFromCode(languageCode, customer?.languages ?? []) ?? ""
    );
  };

  const machineTranslateTaskQueue = useMemo(() => {
    if (token) return new MachineTranslateTaskQueue(token, 3000);
  }, [token]);

  useEffect(() => {
    if (isAuthLoading || !auth) return;
    setEmail(auth.user.email);
  }, [auth, isAuthLoading]);

  useEffect(() => {
    if (dialogRef.current) {
      dialogRef.current.addEventListener("close", () => {
        setDisableModal(false);
      });
    }
  }, [dialogRef]);

  const startTranslate = (): void => {
    setDisableModal(true);
    machineTranslateTaskQueue
      .translateFileAndSendToProofreading(
        sourceFile,
        sourceLanguage,
        targetLanguages,
        sourceContext,
        email,
        urgent,
        deliverInOneFile,
        additionalInstructions,
        configGroupId,
        {
          noTranslateCells,
          languageCells,
        }
      )
      .then(() => {
        onSuccess?.();
      })
      .finally(() => {
        setUrgent(false);
        setDeliverInOneFile(false);
        setAdditionalInstructions("");
      });
  };

  return (
    <Modal {...modalProps} dialogRef={dialogRef} className="tw-relative">
      {disableModal && (
        <div
          className="tw-absolute tw-inset-0 tw-bg-gray-900 tw-z-50"
          data-testid="machine-translate-proofread-disabled-overlay"
        >
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full tw-gap-2 tw-text-white">
            <h4 className="tw-text-center">
              It&apos;s now safe to leave this page.
              <br />
              You will be notified via email when the translation is done and
              ready for proofreading.
            </h4>
            <Button
              variant="primary"
              onClick={(): void => {
                setDisableModal(false);
                dialogRef.current?.close();
              }}
              data-testid="machine-translate-proofread-close-button"
            >
              Close
            </Button>
          </div>
        </div>
      )}
      <Modal.Header divide>
        <h5 data-testid="machine-translate-proofread-modal-title">
          Machine Translate & Send for proofreading
        </h5>
      </Modal.Header>
      <Modal.Body>
        <div className="tw-grid tw-grid-cols-2 tw-gap-0.5">
          <div
            className="tw-ring-1 tw-ring-gray-300 tw-rounded-md tw-p-2 tw-flex tw-items-center tw-justify-center tw-cursor-default"
            data-balloon="Source Language"
            data-balloon-pos="up"
            data-testid="machine-translate-proofread-source-language"
          >
            {renderLanguage(sourceLanguage)}
          </div>
          <div
            className="tw-ring-1 tw-ring-gray-300 tw-rounded-md tw-p-2 tw-flex tw-items-center tw-gap-x-2 tw-flex-wrap tw-cursor-default"
            data-balloon="Target Language(s)"
            data-balloon-pos="up"
            data-testid="machine-translate-proofread-target-languages"
          >
            {targetLanguages.map((language, index) => (
              <span key={language}>
                {renderLanguage(language)}
                {index < targetLanguages.length - 1 && (
                  <>
                    {" "}
                    <span className="tw-text-gray-300">|</span>
                  </>
                )}
              </span>
            ))}
          </div>
        </div>
        {disclaimer}
        <div className="txu-divider" />
        <form className="txu-form tw-flex tw-flex-col tw-gap-4">
          <div>
            <div>
              <label className="!tw-mb-0" htmlFor="email">
                Email address(es) for returning the delivery *
              </label>
              <small>
                Separate multiple email addresses with commas <code>(,)</code>
              </small>
            </div>
          </div>
          <input
            type="email"
            placeholder="Email..."
            className="tw-w-full"
            id="email"
            value={email}
            onChange={(e): void => setEmail(e.target.value)}
            data-testid="machine-translate-proofread-email-input"
          />
          <div className="tw-flex tw-gap-2 tw-items-center [&>*]:!tw-mb-0">
            <ToggleCheckbox
              checked={urgent}
              setChecked={setUrgent}
              checkboxId="urgent-checkbox"
              data-testid="machine-translate-proofread-urgent-toggle"
            />
            <ToggleLabel
              label={
                customer?.config.translation_request_urgent_message ||
                "Urgent Delivery"
              }
              checked={urgent}
              checkboxId="urgent-checkbox"
            />
          </div>
          <div>
            <label htmlFor="additional-instructions">
              Additional instructions
            </label>
            <textarea
              className="tw-w-full"
              value={additionalInstructions}
              onChange={(e): void => setAdditionalInstructions(e.target.value)}
              data-testid="machine-translate-proofread-instructions"
            />
          </div>
          {allowSendAllLanguagesInOneRequest && (
            <div className="tw-flex tw-gap-2 tw-items-center [&>*]:!tw-mb-0">
              <ToggleCheckbox
                checked={deliverInOneFile}
                setChecked={setDeliverInOneFile}
                checkboxId="deliver-in-one-file-checkbox"
                data-testid="machine-translate-proofread-deliver-one-file-toggle"
              />
              <ToggleLabel
                label="Deliver in one file"
                checked={deliverInOneFile}
                checkboxId="deliver-in-one-file-checkbox"
              />
            </div>
          )}
        </form>
      </Modal.Body>
      <Modal.Actions>
        <Button
          onClick={(): void => {
            dialogRef.current?.close();
          }}
          data-testid="machine-translate-proofread-cancel-button"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={startTranslate}
          loading={disableModal}
          disabled={disableModal}
          data-testid="machine-translate-proofread-submit-button"
        >
          Translate & Send for Proofreading
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
